import { CommonModule } from '@angular/common';
import { Component, inject, input, output, signal, type InputSignal, type OutputEmitterRef, type SimpleChanges, type WritableSignal } from '@angular/core';
import { LottieComponent, type AnimationOptions } from 'ngx-lottie';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Order, type PageControl, type TableColumn } from '../../../interfaces/application';
import { finalize } from 'rxjs';
import { ContractService } from '../../../services/contract.service';
import { Contract, ContractStatus } from '../../../interfaces/contract';
import dayjs from 'dayjs';
import { ContractStatusPipe } from "../../pipes/contract-status-pipe";
import { TagModule } from 'primeng/tag';
import { AccordionModule } from 'primeng/accordion';
import { ContractPaymentTypePipe } from "../../pipes/contract-payment-type-pipe";

@Component({
  selector: 'app-table-contracts',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    PaginatorModule,
    LottieComponent,
    ContractStatusPipe,
    TagModule,
    AccordionModule,
    ContractPaymentTypePipe
],
  templateUrl: './table-contracts.html',
  styleUrl: './table-contracts.css',
})
export class TableContracts {

  public emptyLottieOptions: AnimationOptions = {
    path: 'assets/lottie/empty.json',
    loop: true,
    autoplay: true,
  };

  private readonly _contractService: ContractService = inject(ContractService);

  public localLoading: WritableSignal<boolean> = signal<boolean>(false);
  public loading: InputSignal<boolean> = input<boolean>(false);
  public search_term: InputSignal<string> = input<string>('');

  public view: OutputEmitterRef<Contract> = output<Contract>();

  public activeContract: string | null = null;

  public items: Contract[] = [];

  public columns: TableColumn[] = [
    {
      label: 'Título',
      slug: 'title',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Partes',
      slug: 'people',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Data',
      slug: 'created_at',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Tags',
      slug: 'tags',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Validade',
      slug: 'due_date',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Status',
      slug: 'status',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Arquivos',
      slug: 'files',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Ações',
      slug: '',
      align: 'text-start',
      sortable: false,
    },
  ];

  public pageControl: PageControl = {
    page: 0,
    take: 10,
    itemCount: 0,
    pageCount: 0,
    orderField: 'created_at',
    order: Order.DESC
  }

  protected readonly order: typeof Order = Order;
  protected readonly contractStatus: typeof ContractStatus = ContractStatus;

  public ngOnChanges(changes: SimpleChanges): void {
    const {
      loading,
      search_term,
    } = changes;

    if (loading?.previousValue !== null && !loading?.currentValue) {
      this.loadData();
    }
    else if (search_term?.currentValue && search_term?.currentValue !== search_term?.previousValue) {
      this.loadData();
    }
  }

  public loadData(): void {

    const filters = {
      search_term: this.search_term() || '',
    };

    this.localLoading.set(true);
    this._contractService.get(this.pageControl, filters)
      .pipe(finalize(() => this.localLoading.set(false)))
      .subscribe({
        next: response => {
          this.items = response.data;

          this.pageControl.page = response.meta.current_page;
          this.pageControl.itemCount = response.meta.total;
        }
      })
  }

  public sort(slug: string): void {
    this.pageControl.page = 0;
    this.pageControl.orderField = slug;
    this.pageControl.order = this.pageControl.order === Order.ASC ? Order.DESC : Order.ASC;

    this.loadData();
  }

  public onPageChange(e: PaginatorState): void {
    this.pageControl.page = (e?.page || 0) + 1;
    this.pageControl.take = (e?.rows || 10);

    this.loadData();
  }

  public isExpired(dueDate: string): boolean {
    return dayjs(dueDate).isBefore(dayjs());
  }

  public dueDateDiffInMonths(dueDate: string): number {
    return dayjs(dueDate).diff(dayjs(), 'month');
  }

  public setActiveContract(contract: Contract): void {
    if (this.activeContract === contract.id) {
      this.activeContract = null;
    } else {
      this.activeContract = contract.id!;
    }
  }

  public openFile(url: string): void {
    window.open(url, '_blank');
  }
}
